"use client";

import { useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import { readPartnerBookingOrigin } from "@/utils/partner/PartnerContext";

// Customer-facing WhatsApp button.
//
// This used to inject WATI's third-party chat widget (clare.ai script + a
// wati.io brand image). Since the WATI cutover it's a plain wa.me link to the
// Sukuto customer number — no external script, no extra image, so it costs
// nothing on Core Web Vitals and keeps the page fully first-party. The
// visibility rules below are unchanged from the widget era: they are UX rules
// about where a floating button may sit, independent of who powers the chat.
const WHATSAPP_NUMBER = "918884414400"; // +91 88844 14400 (customer number)
const PREFILL = "Hi! I'd like to rent a vehicle.";

// Routes where the floating button competes with the booking UI (it overlaps
// the cart/CTA). Hidden there on MOBILE only — on desktop there's room. The
// mini-cart modal lives on /search, so it's covered.
const isHiddenPath = (pathname) => /(?:\/search|\/cart|\/booking)\/?$/.test(pathname || "");

// Partner white-label storefronts (/partner/<slug>/...) must not show the
// global Sukuto button (Sukuto branding + number). Hidden there on ALL
// viewports, unlike the funnel rule above which is mobile-only.
const isPartnerPath = (pathname) => /^\/partner(?:\/|$)/.test(pathname || "");

// The shared checkout funnel (/cart, /booking) and the user account pages
// (/user/...) live OUTSIDE /partner/<slug>/, but when the session started from
// a partner storefront (sukuto_partner_origin cookie) they stay white-labelled
// — so the Sukuto button is hidden there on ALL viewports too (covering
// desktop), not just mobile like the rule above.
const isFunnelPath = (pathname) =>
  /(?:\/cart|\/booking)\/?$/.test(pathname || "") ||
  /^\/user(?:\/|$)/.test(pathname || "");

// "mobile" = below the lg breakpoint (matches MobileBar / NavigationBar split).
const MOBILE_QUERY = "(max-width: 1023px)";
const NOOP_SUBSCRIBE = () => () => {};

// SSR-safe reads via useSyncExternalStore — no setState-in-effect, no
// hydration warning. hasMounted returns false on the server / first hydration
// pass, so the button is emitted only client-side (no CLS, and the client-only
// reads below — viewport, partner-origin cookie — can never mismatch the SSR
// HTML).
const useHasMounted = () => useSyncExternalStore(NOOP_SUBSCRIBE, () => true, () => false);
const useIsMobile = () =>
  useSyncExternalStore(
    (onChange) => {
      const mq = window.matchMedia(MOBILE_QUERY);
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    },
    () => window.matchMedia(MOBILE_QUERY).matches,
    () => false
  );

const WhatsAppButton = () => {
  const hasMounted = useHasMounted();
  const isMobileView = useIsMobile();
  const pathname = usePathname();
  const isPartnerOrigin = !!readPartnerBookingOrigin();

  const shouldHide =
    isPartnerPath(pathname) ||
    (isHiddenPath(pathname) && isMobileView) ||
    (isFunnelPath(pathname) && isPartnerOrigin);

  if (!hasMounted || shouldHide) return null;

  const href = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(PREFILL)}`;

  return (
    <a
      // Stable id so an open modal can hide this (globals.css,
      // `html[data-scroll-locked]`). The WATI widget this replaced had its own id
      // and the old selector relied on it; without one, the FAB floated over the
      // search sheet at z-index 999.
      id="whatsapp-float-button"
      href={href}
      target="_blank"
      rel="noopener noreferrer nofollow"
      aria-label="Chat with us on WhatsApp"
      style={{
        position: "fixed",
        right: "20px",
        bottom: isMobileView ? "90px" : "20px", // lift above MobileBar on mobile
        zIndex: 999,
        width: "56px",
        height: "56px",
        borderRadius: "50%",
        backgroundColor: "#25D366", // WhatsApp brand green
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
        color: "#fff",
      }}
    >
      <svg viewBox="0 0 24 24" width="30" height="30" fill="currentColor" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.359.101 11.945c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652a11.882 11.882 0 005.71 1.447h.006c6.585 0 11.946-5.36 11.949-11.945a11.821 11.821 0 00-3.495-8.4z" />
      </svg>
    </a>
  );
};

export default WhatsAppButton;
